/**
 * OverlayTrigger2 - Experimental compatibility wrapper using Overlay2
 *
 * This is a parallel implementation to test the new Waypoint-based overlay system.
 * Use feature flags to test this in apps before replacing the original OverlayTrigger.
 */

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { IOverlay2Props, Overlay2, Overlay2Children, Placement2 } from './Overlay2';
import logger from '../util/logger';

export type TriggerType = 'click' | 'hover' | 'focus';

export interface IOverlayTrigger2Handle {
  hide: () => void;
}

export interface IOverlayTrigger2Props {
  /** The trigger element (must be a single React element) */
  children: React.ReactElement;

  /** Default show state for uncontrolled mode */
  defaultShow?: boolean;

  /** Delay in ms before showing/hiding */
  delay?: number | { show: number; hide: number };

  /**
   * Enable flipping to opposite side when there isn't enough space
   * Note: Waypoint always flips automatically, so this prop is accepted for API compatibility but ignored
   */
  flip?: boolean;

  /** Hide overlay on window resize */
  hideOnWindowResize?: boolean;

  /** The overlay content - can be element or render function */
  overlay: Overlay2Children;

  /** Additional overlay props */
  overlayProps?: Partial<IOverlay2Props>;

  /** Placement of overlay relative to trigger element */
  placement?: Placement2;

  /** Close overlay when clicking outside */
  rootClose?: boolean;

  /** Controlled show state */
  show?: boolean;

  /** How to trigger the overlay - can be one or multiple */
  trigger?: TriggerType | TriggerType[];

  /** Callback when the user clicks the trigger */
  onClick?: (event: React.MouseEvent) => void;

  /** Callback when show state changes */
  onToggle?: (show: boolean) => void;
}

/**
 * OverlayTrigger2 - Trigger-based overlay component
 *
 * API-compatible with the original OverlayTrigger, but uses Overlay2/Waypoint underneath.
 */
export const OverlayTrigger2 = forwardRef<IOverlayTrigger2Handle, IOverlayTrigger2Props>((props, ref) => {
  const {
    children,
    defaultShow = false,
    delay = 0,
    hideOnWindowResize = false,
    overlay,
    placement = 'top',
    overlayProps = {},
    show: controlledShow,
    trigger = 'hover',
    onClick,
    onToggle
  } = props;

  // State management
  const [uncontrolledShow, setUncontrolledShow] = useState(defaultShow);
  const isControlled = controlledShow !== undefined;
  const show = isControlled ? controlledShow : uncontrolledShow;

  // Arrow management - we show the arrow unless the hover trigger is used
  let showArrow = true;

  // Refs
  const triggerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<number | null>(null);

  // Expose imperative handle for programmatic control
  useImperativeHandle(ref, () => ({
    hide() {
      if (show) {
        // Just update state - onToggle will be called by the effect below
        if (!isControlled) {
          setUncontrolledShow(false);
        }
      }
    }
  }), [show, isControlled]);

  // Call onToggle when show state changes (matches original OverlayTrigger behavior)
  useEffect(() => {
    onToggle?.(show);
  }, [show, onToggle]);

  // Helper to update show state with optional delay
  const updateShow = useCallback((nextShow: boolean) => {
    logger('OverlayTrigger2.updateShow(): Entering. nextShow =', nextShow);

    // Clear any pending timeout
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const delayMs = typeof delay === 'number'
      ? delay
      : (nextShow ? delay?.show : delay?.hide) ?? 0;

    const update = () => {
      if (!isControlled) {
        setUncontrolledShow(nextShow);
        // onToggle is called by the effect when state changes
      }
      else {
        // In controlled mode, notify parent but don't change internal state
        onToggle?.(nextShow);
      }
    };

    if (delayMs > 0) {
      timeoutRef.current = window.setTimeout(update, delayMs);
    }
    else {
      update();
    }
  }, [delay, isControlled, onToggle]);

  const handleShow = useCallback((event?) => {
    logger('OverlayTrigger2.handleShow(): Entering. event =', event);
    updateShow(true);
  }, [updateShow]);

  const handleHide = useCallback((event?) => {
    logger('OverlayTrigger2.handleHide(): Entering. event =', event);

    if (event && event.type !== 'sdfAfterClose') {
      // Use the same box as the target element
      const rect = triggerRef.current?.firstElementChild.getBoundingClientRect();
      const mouseX = event.clientX;
      const mouseY = event.clientY;

      logger('OverlayTrigger2.handleHide():', rect, mouseX, mouseY);

      // Added one to mouseY to deal with non-integer pixel values that trigger mouse leave at
      // the bottom of element, but mouseY is still viewed as 'inside' the box.
      if (mouseX >= rect?.left && mouseX <= rect?.right && mouseY >= rect?.top && (mouseY + 1) <= rect?.bottom) {
        logger('OverlayTrigger2.handleHide(): Mouse is inside the element!');
      }
      else {
        logger('OverlayTrigger2.handleHide(): Closing because mouse is outside the element.');
        updateShow(false);
      }
    }
    else if (!isControlled) {
      // The floating pane will close itself and fire sdfAfterClose before the click event
      // arrives, so it will happen first, setting uncontrolledShow to false. Then, the click
      // event will toggle uncontrolledShow back to true. So, we need setTimeout to fix the order.
      setTimeout(() => {
        logger('OverlayTrigger2.handleHide(): Closing because element is uncontrolled and we need the right state.');
        updateShow(false);
      }, 20);
    }
  }, [updateShow]);

  // Handle window resize to hide overlay if configured
  const handleWindowResize = useCallback((event) => {
    if (hideOnWindowResize) {
      handleHide(event);
    }
  }, [hideOnWindowResize, handleHide]);

  // Add/remove window resize listener
  useEffect(() => {
    if (hideOnWindowResize) {
      window.addEventListener('resize', handleWindowResize);
    }

    return () => {
      if (hideOnWindowResize) {
        window.removeEventListener('resize', handleWindowResize);
      }
    };
  }, [hideOnWindowResize, handleWindowResize]);

  // Toggle between show/hide
  const handleToggle = useCallback((event: React.MouseEvent) => {
    logger('OverlayTrigger2.handleToggle(): Entering. event =', event);
    event.preventDefault();
    event.stopPropagation();

    updateShow(!show);
    onClick?.(event);
  }, [handleShow, handleHide, onClick]);

  // Build event handlers based on trigger type(s)
  const triggers = Array.isArray(trigger) ? trigger : [trigger];
  const triggerProps: Record<string, any> = {};

  if (triggers.includes('click')) {
    triggerProps.onClick = handleToggle;
  }

  if (triggers.includes('hover')) {
    showArrow = false;

    triggerProps.onMouseEnter = useCallback((event: React.MouseEvent) => {
      logger('OverlayTrigger2.onMouseEnter(): Entering.', event);
      handleShow(event);
    }, [handleShow]);

    triggerProps.onMouseLeave = useCallback((event: React.MouseEvent) => {
      logger('OverlayTrigger2.onMouseLeave(): Entering.', event);
      handleHide(event);
    }, [handleHide]);
  }

  if (triggers.includes('focus')) {
    triggerProps.onFocus = handleShow;
    triggerProps.onBlur = handleHide;
  }

  logger('OverlayTrigger2.render(): show =', show);

  // This makes sure that the floating pane is attached to the 'real' trigger element
  // instead of the wrapper element used to manage events. Otherwise, the floating pane
  // could be very disconnected from the real anchor if it has margin or padding.
  // const targetNode = triggerRef.current?.firstElementChild;

  // The trigger element needs a known wrapper on it to add the necessary
  // event handlers and ref that can get passed to the Overlay2 component.
  return (
    <>
      <div
        className="contents"
        ref={triggerRef}
        role="button"
        {...triggerProps}
      >
        {children}
      </div>
      <Overlay2
        placement={placement}
        show={show}
        showArrow={showArrow}
        target={triggerRef.current?.firstElementChild}
        onHide={handleHide}
        {...overlayProps}
      >
        {overlay}
      </Overlay2>
    </>
  );
});

OverlayTrigger2.displayName = 'OverlayTrigger2';
